import * as checkoutApi from './checkout-api.js';
import * as stripe from './stripe.js';

export function awaitOrderCreation(sessionId, { onComplete, onFailed, onTimeout } = {}) {
    let stopped = false;
    let interval = null;
    let elapsed = 0;
    const intervalMs = 1000;
    const timeoutMs = 90000;

    const onVisible = () => {
        if (document.visibilityState === 'visible') poll();
    };

    const stop = () => {
        if (stopped) return;
        stopped = true;
        if (interval) clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
    };

    const poll = async () => {
        if (stopped) return;

        let session;
        try {
            session = await checkoutApi.getSession(sessionId);
        } catch {
            return;
        }

        if (stopped) return;

        if (session.status === 'complete') {
            stop();
            onComplete?.(session);
            return;
        }

        if (session.status === 'failed') {
            stop();
            onFailed?.({
                messages: ['Something went wrong during the order creation process and an automatic refund has been initiated.'],
                next_url: '/',
                status: 'ORDER_CREATION_FAILED',
            });
        }

        // 'active' or 'processing' — the webhook has not finished yet, keep waiting.
    };

    interval = setInterval(() => {
        elapsed += intervalMs;

        if (elapsed >= timeoutMs) {
            stop();
            onTimeout?.();
            return;
        }

        poll();
    }, intervalMs);

    document.addEventListener('visibilitychange', onVisible);
}

export function decideNextAction(data, { hasPaymentIntent = false } = {}) {
    const status = data?.status;
    const sessionId = typeof data?.session_id === 'string' && data.session_id ? data.session_id : null;

    if (status === 'ORDER_PLACED' || status === 'PAYMENT_IN_PROGRESS') {
        return { type: 'await', sessionId };
    }

    if (status === 'PAYMENT_REQUIRES_ACTION') {
        return { type: 'requiresAction', clientSecret: data.payment?.client_secret || null };
    }

    if (status === 'ALREADY_PAID' && sessionId) {
        return { type: 'await', sessionId };
    }

    if (status === 'BASKET_MODIFIED' && hasPaymentIntent) {
        return { type: 'await', sessionId };
    }

    return { type: 'fail', data };
}

export async function handlePaymentAction(clientSecret) {
    const { error, paymentIntent } = await stripe.handleNextAction(clientSecret);

    return { error, paymentIntent };
}

export async function submitOrder(form, formData) {
    return checkoutApi.submit(form, formData);
}
